import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Title } from '../components/ui/Title';
import { GENRES, SKILLS, BAND_STATUSES } from '../constants/music';
import { useMusicTranslations } from '../utils/translations';
import { useProfile } from '../hooks/useProfile';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { LocationSearch } from '../components/LocationSearch';
import { Users } from 'lucide-react';
import { optimizeImage, validateImageFile } from '../utils/imageOptimizer';
import type { Band } from '../types/database';

const currentYear = new Date().getFullYear();

const createBandSchema = z.object({
  name: z.string().min(3, 'Band name must be at least 3 characters').max(50, 'Band name cannot exceed 50 characters'),
  description: z.string().min(10, 'Description must be at least 10 characters').max(500, 'Description cannot exceed 500 characters'),
  location: z.string().min(2, 'Location must be at least 2 characters').max(100, 'Location cannot exceed 100 characters'),
  coordinates: z.object({
    latitude: z.number(),
    longitude: z.number(),
  }).nullable(),
  genres: z.array(z.string()).min(1, 'Please select at least one genre'),
  instruments: z.array(z.string()).min(1, 'Please select at least one instrument/role'),
  avatar_url: z.string().optional(),
  foundation_year: z.union([
    z.number().int().min(1900).max(currentYear).optional(),
    z.literal('')
  ]).transform(val => val === '' ? null : val),
  website_url: z.union([
    z.string().url('Please enter a valid URL').optional(),
    z.literal('')
  ]).transform(val => val === '' ? null : val),
  status: z.string().min(1, 'Please select a status'),
});

type CreateBandFormData = z.infer<typeof createBandSchema>;

function CreateBand() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useProfile();
  const { getTranslatedGenres, getTranslatedBandStatuses } = useMusicTranslations();
  const [error, setError] = React.useState('');
  const [uploading, setUploading] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<CreateBandFormData>({
    resolver: zodResolver(createBandSchema),
    defaultValues: {
      genres: [],
      instruments: [],
      coordinates: null,
      foundation_year: '',
      website_url: '',
      status: 'Aktiv',
    },
  });

  const watchGenres = watch('genres', []);
  const watchInstruments = watch('instruments', []);

  const translatedGenres = getTranslatedGenres();
  const translatedBandStatuses = getTranslatedBandStatuses();

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      setError('');

      let file = event.target.files?.[0];
      if (!file) return;

      await validateImageFile(file);

      // Optimize image before upload
      const optimized = await optimizeImage(file, {
        maxWidth: 500,
        maxHeight: 500,
        quality: 0.85,
        format: 'image/webp',
      });
      file = optimized.file;

      // Generate unique file name without special characters
      const fileName = `${uuidv4().replace(/-/g, '')}.webp`;

      // Upload file to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('bands')
        .upload(fileName, file, {
          cacheControl: '31536000',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('bands')
        .getPublicUrl(fileName);

      // Update form with avatar URL
      setValue('avatar_url', publicUrl);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleGenreChange = (genre: string) => {
    const currentGenres = watchGenres;
    const newGenres = currentGenres.includes(genre)
      ? currentGenres.filter(g => g !== genre)
      : [...currentGenres, genre];
    setValue('genres', newGenres);
  };

  const handleInstrumentChange = (instrument: string) => {
    const currentInstruments = watchInstruments;
    const newInstruments = currentInstruments.includes(instrument)
      ? currentInstruments.filter(i => i !== instrument)
      : [...currentInstruments, instrument];
    setValue('instruments', newInstruments);
  };

  const handleLocationChange = (location: { name: string; coordinates: { latitude: number; longitude: number } }) => {
    setValue('location', location.name);
    setValue('coordinates', location.coordinates);
  };

  const onSubmit = async (data: CreateBandFormData) => {
    if (isSubmitting) return; // Prevent multiple submissions
    
    if (!user || !profile) {
      setError(t('bands.create.loginRequired'));
      return;
    }


    try {
      setIsSubmitting(true);
      // Start a Supabase transaction
      const { data: band, error: bandError } = await supabase
        .from('bands')
        .insert([
          {
            name: data.name,
            description: data.description,
            location: data.location,
            coordinates: data.coordinates,
            genres: data.genres,
            avatar_url: data.avatar_url,
            created_by: profile.id,
            foundation_year: data.foundation_year,
            website_url: data.website_url,
            status: data.status,
          },
        ])
        .select()
        .single();

      if (bandError) throw bandError;
      if (!band) throw new Error('Failed to create band');

      // Add the creator as a band member with admin role
      const { error: memberError } = await supabase
        .from('band_members')
        .insert([
          {
            band_id: band.id,
            profile_id: profile.id,
            role: 'admin',
            instruments: data.instruments,
          },
        ]);

      if (memberError) throw memberError;

      navigate(`/bands/${band.slug}`);
    } catch (err) {
      console.error('Error creating band:', err);
      setError(err instanceof Error ? err.message : 'Failed to create band');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter available instruments based on musician's skills
  const availableInstruments = profile?.skills || [];

  const breadcrumbItems = [
    { label: t('nav.bands'), href: '/bands' },
    { label: t('bands.createBand') },
  ];

  return (
    <div className="container mx-auto px-6 pb-8 md:py-24">
      <div className="mb-6">
        <Breadcrumbs items={breadcrumbItems} />
      </div>

      <Title subtitle={t('bands.createBand')}>
        {t('bands.createBand')}
      </Title>

      <div className="max-w-4xl mx-auto">
        {error && (
          <div className="bg-red-500/10 text-red-500 p-4 rounded-lg mb-6 text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          <div className="panel">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-32 h-32 rounded-lg bg-gray-custom/10 flex items-center justify-center overflow-hidden">
                {watch('avatar_url') ? (
                  <img
                    src={watch('avatar_url')}
                    alt="Band avatar"
                    className="w-full h-full object-cover"
                   loading="lazy" />
                ) : (
                  <Users className="w-12 h-12 text-light/60" />
                )}
              </div>
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="btn-outline"
                >
                  {uploading ? t('forms.uploading') : t('forms.uploadBandPhoto')}
                </button>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-light/80">
                  {t('forms.bandName')}
                </label>
                <input
                  type="text"
                  id="name"
                  {...register('name')}
                  placeholder={t('forms.enterBandName')}
                  className="mt-1 block w-full rounded-lg bg-dark border border-gray-custom/20 px-4 py-2 text-light focus:border-gray-custom/40 focus:outline-none"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-light/80 mb-2">
                  {t('forms.location')}
                </label>
                <LocationSearch
                  value={watch('location') || ''}
                  onChange={handleLocationChange}
                  error={errors.location?.message}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="foundation_year" className="block text-sm font-medium text-light/80">
                    {t('common.foundationYear')} ({t('common.optional')})
                  </label>
                  <input
                    type="number"
                    id="foundation_year"
                    min="1900"
                    max={currentYear}
                    {...register('foundation_year', { valueAsNumber: true })}
                    className="mt-1 block w-full rounded-lg bg-dark border border-gray-custom/20 px-4 py-2 text-light focus:border-gray-custom/40 focus:outline-none"
                    placeholder="2025"
                  />
                  {errors.foundation_year && (
                    <p className="mt-1 text-sm text-red-500">{errors.foundation_year.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="website_url" className="block text-sm font-medium text-light/80">
                    {t('common.websiteUrl')} ({t('common.optional')})
                  </label>
                  <input
                    type="url"
                    id="website_url"
                    {...register('website_url')}
                    className="mt-1 block w-full rounded-lg bg-dark border border-gray-custom/20 px-4 py-2 text-light focus:border-gray-custom/40 focus:outline-none"
                    placeholder="https://example.com"
                  />
                  {errors.website_url && (
                    <p className="mt-1 text-sm text-red-500">{errors.website_url.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-light/80">
                  {t('forms.bandDescription')}
                </label>
                <textarea
                  id="description"
                  rows={4}
                  {...register('description')}
                  className="mt-1 block w-full rounded-lg bg-dark border border-gray-custom/20 px-4 py-2 text-light focus:border-gray-custom/40 focus:outline-none"
                  placeholder={t('forms.tellAboutBand')}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-500">{errors.description.message}</p>
                )}
              </div>
            </div>
          </div>


          <div className="panel">
            <h3 className="text-xl font-semibold mb-6">{t('forms.bandGenres')}</h3>
            <div className="grid grid-cols-3 gap-4">
              {translatedGenres.map((genre) => (
                <label key={genre.value} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={watchGenres.includes(genre.value)}
                    onChange={() => handleGenreChange(genre.value)}
                    className="h-4 w-4 rounded border-gray-custom/20 text-primary focus:ring-primary"
                  />
                  <span className="text-light/80">{genre.label}</span>
                </label>
              ))}
            </div>
            {errors.genres && (
              <p className="mt-4 text-sm text-red-500">{errors.genres.message}</p>
            )}
          </div>

          <div className="panel">
            <h3 className="text-xl font-semibold mb-6">{t('forms.yourRoles')}</h3>
            <div className="grid grid-cols-3 gap-4">
              {availableInstruments.map((instrument) => (
                <label key={instrument} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={watchInstruments.includes(instrument)}
                    onChange={() => handleInstrumentChange(instrument)}
                    className="h-4 w-4 rounded border-gray-custom/20 text-primary focus:ring-primary"
                  />
                  <span className="text-light/80">{instrument}</span>
                </label>
              ))}
            </div>
            {errors.instruments && (
              <p className="mt-4 text-sm text-red-500">{errors.instruments.message}</p>
            )}
          </div>

          <div className="panel">
            <h3 className="text-xl font-semibold mb-6">Status</h3>
            <div className="grid grid-cols-2 gap-4">
              {translatedBandStatuses.map((status) => (
                <label key={status.value} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    value={status.value}
                    {...register('status')}
                    className="h-4 w-4 border-gray-custom/20 text-primary focus:ring-primary"
                  />
                  <span className="text-light/80">{status.label}</span>
                </label>
              ))}
            </div>
            {errors.status && (
              <p className="mt-4 text-sm text-red-500">{errors.status.message}</p>
            )}
          </div>

          <div className="flex flex-col md:flex-row justify-end md:space-x-4">
            <button
              type="button"
              onClick={() => navigate('/bands')}
              className="btn-outline order-2 mt-6 md:mt-0"
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </button>
            <button 
              type="submit" 
              className="btn-secondary md:order-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('common.saving') : t('forms.createBand')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateBand;